import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/lib/toast';
import i18n from '@/i18n';
import {
  Brand,
  CreateBrandRequest,
  UpdateBrandRequest,
  Post,
  CreatePostRequest,
  UpdatePostRequest,
  GeneratePostsRequest,
  GeneratePostsResponse,
  Workspace,
  CreateWorkspaceRequest,
  DashboardStats,
  User,
  PlatformConnection,
  OnboardingProfile,
  ApiKey,
  DataEventItem,
  ContentCadence,
  UpsertCadenceRequest,
  ReviewPost,
  AnalyticsOverview,
  PostAnalyticsItem,
  PostTimeseriesPoint,
  PlatformTimeseriesPoint,
  LinkedInOrgSummary,
  LinkedInOrgHistory,
  SocialPlatform,
  AdCampaign,
  AdSet,
  AdCreative,
  CampaignInsightsResponse,
  AdminUser,
  AdminUsageOverview,
  ManualPostRequest,
  WorkspaceAiUsage,
  AdminAiUsageSummary,
  AdminAiSettingsResponse,
  UpdateAdminAiSettingsRequest,
  AdminWorkspaceAiUsage,
  SchedulerConfig,
  ApiCallLogRow,
  ApiCallLogStats,
  ImportJob,
  BlogAnalyticsSummary,
} from '@/types';

import { api } from './client';

export const apiKeyApi = {
  list: (workspaceId: string) =>
    api.get<ApiKey[]>(`/api/workspaces/${workspaceId}/api-keys`).then((r) => r.data),
  create: (workspaceId: string, name: string) =>
    api
      .post<{
        id: string;
        name: string;
        key: string;
        keyPrefix: string;
      }>(`/api/workspaces/${workspaceId}/api-keys`, { name })
      .then((r) => r.data),
  delete: (workspaceId: string, keyId: string) =>
    api.delete(`/api/workspaces/${workspaceId}/api-keys/${keyId}`).then((r) => r.data),
};

export const dataEventApi = {
  list: (workspaceId: string) =>
    api.get<DataEventItem[]>(`/api/workspaces/${workspaceId}/events`).then((r) => r.data),
};

export const cadenceApi = {
  /** Workspace-wide list — feeds the brand-card row at the top of CadencePage. */
  list: (workspaceId: string) =>
    api.get<ContentCadence[]>(`/api/workspaces/${workspaceId}/cadences`).then((r) => r.data),
  /** Per-brand list — used when a brand card is selected on CadencePage. */
  listForBrand: (workspaceId: string, brandId: string) =>
    api
      .get<ContentCadence[]>(`/api/workspaces/${workspaceId}/brands/${brandId}/cadences`)
      .then((r) => r.data),
  /** PUT upsert with the platform in the URL — preferredDays goes through as
   *  an array; the backend joins it with commas before persisting. */
  upsert: (workspaceId: string, brandId: string, platform: string, data: UpsertCadenceRequest) =>
    api
      .put<ContentCadence>(
        `/api/workspaces/${workspaceId}/brands/${brandId}/cadences/${platform}`,
        data,
      )
      .then((r) => r.data),
  delete: (workspaceId: string, brandId: string, platform: string) =>
    api
      .delete(`/api/workspaces/${workspaceId}/brands/${brandId}/cadences/${platform}`)
      .then((r) => r.data),
  /** Cross-brand distribution per cadence (platform). */
  getDistribution: (workspaceId: string, brandId: string, platform: string) =>
    api
      .get<
        { aboutBrandId: string; percentage: number; aboutBrandName: string }[]
      >(`/api/workspaces/${workspaceId}/brands/${brandId}/cadences/${platform}/distribution`)
      .then((r) => r.data),
  setDistribution: (
    workspaceId: string,
    brandId: string,
    platform: string,
    entries: { aboutBrandId: string; percentage: number }[],
  ) =>
    api
      .put(
        `/api/workspaces/${workspaceId}/brands/${brandId}/cadences/${platform}/distribution`,
        entries,
      )
      .then((r) => r.data),
  /**
   * Run the weekly cadence generation now, outside the Monday-06:00 cron.
   * V154 (2026-05-22) — async dispatch: backend returns 202 immediately,
   * posts land in PENDING review state on the Review page as they
   * complete (typically over 1-5 minutes). Callers should toast "started"
   * and refresh the Review page, not wait for a count.
   */
  runNow: (workspaceId: string) =>
    api
      .post<{
        status: string;
        workspaceId: string;
      }>(`/api/workspaces/${workspaceId}/cadences/run-now`, {})
      .then((r) => r.data),
};

// ── V73 — Top-up packs ──────────────────────────────────────────────────

export interface PublicTopupPack {
  packKey: string;
  displayName: string;
  units: number;
  priceEur: number;
  resource: string;
  aiModelTier?: string | null;
}

export interface WorkspaceTopupBalance {
  resource: string;
  aiModelTier: string | null;
  unitsAvailable: number;
}

export interface WorkspaceTopupPurchase {
  id: string;
  packKey: string;
  displayName: string;
  unitsPurchased: number;
  unitsConsumed: number;
  pricePaidEur: number;
  mollieStatus: string;
  purchasedAt: string | null;
  paidAt: string | null;
  expiresAt: string | null;
}

export interface WorkspaceTopupsResponse {
  balances: WorkspaceTopupBalance[];
  recentPurchases: WorkspaceTopupPurchase[];
}

export interface TopupCheckoutResponse {
  checkoutUrl: string;
  topupPurchaseId: string;
}

export const topupApi = {
  /** Balances + recent purchases for the active workspace. */
  getWorkspaceTopups: (workspaceId: string) =>
    api.get<WorkspaceTopupsResponse>(`/api/workspaces/${workspaceId}/topups`).then((r) => r.data),

  /** Create a Mollie one-off payment. Caller redirects to checkoutUrl. */
  checkout: (workspaceId: string, packKey: string) =>
    api
      .post<TopupCheckoutResponse>(`/api/billing/workspaces/${workspaceId}/topups/checkout`, {
        packKey,
      })
      .then((r) => r.data),
};

// Admin (read from /api/public/plans for the pack catalog, or the admin
// endpoint below for full editor shape including inactive packs)
export interface AdminTopupPack {
  packKey: string;
  resourceType: string;
  aiModelTier: string | null;
  displayName: string;
  sortOrder: number;
  isActive: boolean;
  units: number;
  priceEur: number;
  unitCostEur: number;
  marginPercent: number;
  updatedAt: string | null;
}

export interface AdminTopupsResponse {
  packs: AdminTopupPack[];
  updatedAt: string | null;
}

export interface TopupPackPatch {
  displayName?: string;
  sortOrder?: number;
  isActive?: boolean;
  units?: number;
  priceEur?: number;
  unitCostEur?: number;
}

export const adminTopupsApi = {
  getAll: () => api.get<AdminTopupsResponse>('/api/admin/topups').then((r) => r.data),
  patchPack: (packKey: string, patch: TopupPackPatch) =>
    api.patch<AdminTopupPack>(`/api/admin/topups/${packKey}`, patch).then((r) => r.data),
};

export const reviewApi = {
  list: (workspaceId: string) =>
    api.get<ReviewPost[]>(`/api/workspaces/${workspaceId}/posts/review`).then((r) => r.data),
  approve: (workspaceId: string, postId: string) =>
    api.post(`/api/workspaces/${workspaceId}/posts/${postId}/approve`, {}).then((r) => r.data),
  reject: (workspaceId: string, postId: string) =>
    api.post(`/api/workspaces/${workspaceId}/posts/${postId}/reject`, {}).then((r) => r.data),
  regenerate: (workspaceId: string, postId: string) =>
    api.post(`/api/workspaces/${workspaceId}/posts/${postId}/regenerate`, {}).then((r) => r.data),
  // Move an APPROVED + SCHEDULED post's scheduled_at to now so the
  // publisher job picks it up on the next 60s tick. 409 if the post
  // isn't in that shape — backend surfaces the current state in the
  // response body for the UI to render an inline explanation.
  publishNow: (workspaceId: string, postId: string) =>
    api
      .post<Post>(`/api/workspaces/${workspaceId}/posts/${postId}/publish-now`, {})
      .then((r) => r.data),
  /**
   * V135 — confirm a MANUAL_PUBLISH post was published by the user
   * (LinkedIn share-API mode + company-page). Backend flips status
   * to PUBLISHED and stamps `manuallyPublishedAt`.
   */
  confirmManualPublish: (workspaceId: string, postId: string) =>
    api
      .post<Post>(`/api/workspaces/${workspaceId}/posts/${postId}/confirm-manual-publish`, {})
      .then((r) => r.data),
  // Republish a PUBLISHED or FAILED post. Best-effort delete on the
  // platform first, then the row resets to SCHEDULED + scheduled_at=now
  // so the scheduler picks it up on the next 60s tick. 409 on any other
  // status. Owner-only (destructive on platform side).
  republish: (workspaceId: string, postId: string) =>
    api
      .post<Post>(`/api/workspaces/${workspaceId}/posts/${postId}/republish`, {})
      .then((r) => r.data),
  generateVisual: (workspaceId: string, postId: string) =>
    api
      .post<Post>(`/api/workspaces/${workspaceId}/posts/${postId}/generate-visual`, {})
      .then((r) => r.data),
  // Backend route is `/regenerate-reel` (the older `/generate-reel` 404'd).
  generateReel: (workspaceId: string, postId: string) =>
    api
      .post<{
        status: string;
      }>(`/api/workspaces/${workspaceId}/posts/${postId}/regenerate-reel`, {})
      .then((r) => r.data),
};
